
export type UserRole =
    | 'SuperAdmin'
    | 'Admin'
    | 'Editor'
    | 'Teacher'
    | 'Student'
    | 'Partner'
    | 'Old Teacher'
    | 'Old Student'

export interface User {
    id: string
    username: string
    email: string
    firstName: string
    lastName: string
    birthDate?: string
    workDate?: string
    roles: UserRole[]
    avatar?: string
    proImage?: string // Professional Image
    banner?: string
    bio?: string
    jobTitle?: string
    workplaces?: string
    avatarAlt?: string
    proImageAlt?: string
    bannerAlt?: string
    status: 'Active' | 'Pending' | 'Disabled'
    isActive?: boolean
    modules?: string[] // List of accessible module keys
    token?: string
    linkedin?: string
    createdAt?: string
    schoolYearStartDate?: string
    schoolYearEndDate?: string
}

const users = ref<User[]>([])

// Current Session
const currentUser = ref<User | null>(null)

import { detailedDiff } from '../utils/diff'


export const useAuthService = () => {
    const { logAction } = useActivityLog()

    const fetchUsers = async () => {
        try {
            const apiUsers = await $api<any[]>('/users')
            const mappedUsers: User[] = apiUsers.map(u => ({
                id: u.id,
                username: u.username,
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
                roles: u.roles,
                status: u.status,
                avatar: u.avatar,
                proImage: u.proImage,
                banner: u.banner,
                birthDate: u.birthDate,
                workDate: u.workDate,
                isActive: u.isActive,
                bio: u.bio,
                jobTitle: u.jobTitle,
                workplaces: u.workplaces,
                linkedin: u.linkedin,
                avatarAlt: u.avatarAlt,
                proImageAlt: u.proImageAlt,
                bannerAlt: u.bannerAlt,
                modules: u.modules || [],
                createdAt: u.createdAt,
                schoolYearStartDate: u.schoolYearStartDate,
                schoolYearEndDate: u.schoolYearEndDate
            }))

            users.value = mappedUsers
        } catch (e) {
            console.error('Failed to fetch users:', e)
        }
    }

    const refreshPending = async () => {
        await fetchUsers()
    }

    const getUsers = () => {
        if (users.value.length === 0) fetchUsers()
        return users
    }

    const inviteUser = async (email: string, roles: UserRole[], modules: string[] = []) => {
        console.log('[useAuthService] inviteUser called with', email, roles)

        try {
            console.log('[useAuthService] Calling /auth/invite endpoint...')
            const response = await $api<{ token: string }>('/auth/invite', {
                method: 'POST',
                body: {
                    email,
                    roles,
                    modules
                }
            })
            console.log('[useAuthService] /auth/invite call success, token:', response.token)

            await refreshPending()
            return { token: response.token, userId: (response as any).userId } // Cast as any because generic type might not match immediately
        } catch (e) {
            console.error('[useAuthService] inviteUser Error:', e)
            throw e
        }
    }

    const getInvitationInfo = async (token: string) => {
        try {
            return await $api<{ email: string, firstName: string, lastName: string, roles: string[], modules: string[] }>(`/auth/invitation/${token}`)
        } catch (e) {
            console.error('Failed to get invitation info:', e)
            return null
        }
    }

    const completeSetup = async (token: string, data: { username: string, password: string, firstName: string, lastName: string }) => {
        // 1. Validate Token & Get Info
        const info = await $api<{ email: string, roles: string[], modules: string[] }>(`/auth/invitation/${token}`)

        if (!info) throw new Error('Invalid or expired token')

        // 2. Call API to Activate User
        const response = await $api<{ token: string, user: any }>('/auth/setup', {
            method: 'POST',
            body: {
                username: data.username,
                email: info.email, // Use email from trusted token info
                password: data.password,
                firstName: data.firstName,
                lastName: data.lastName,
                roles: info.roles
            }
        })

        const newUser: User = {
            id: response.user.id,
            username: response.user.username,
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            roles: response.user.roles,
            status: 'Active',
            modules: info.modules,
            avatar: response.user.avatar
        }

        // Auto login with token from API
        const cookie = useCookie('auth_token', { path: '/' })
        cookie.value = response.token
        currentUser.value = newUser

        // Update local state
        // Find if user was in pending list logic? 
        // We probably don't have it in local state if we just arrived, unless we fetched users.
        await fetchUsers()

        logAction({
            action: 'CREATE',
            entityType: 'Utilisateur',
            entityId: newUser.id,
            details: `Activation de l'utilisateur "${newUser.firstName} ${newUser.lastName}"`,
        })

        return newUser
    }





    const login = async (identifier: string, password: string, rememberMe: boolean = false) => {
        try {
            const response = await $api<{ token: string, refreshToken?: string, user: any }>('/auth/login', {
                method: 'POST',
                body: { identifier, password, rememberMe }
            })

            // Main token (session)
            const tokenCookie = useCookie('auth_token', { path: '/' })
            tokenCookie.value = response.token

            // Refresh token (long-lived)
            const refreshCookie = useCookie('refresh_token', { path: '/', maxAge: 60 * 60 * 24 * 7 })
            if (response.refreshToken) {
                refreshCookie.value = response.refreshToken
            } else {
                refreshCookie.value = null
            }

            // Map API UserDto to Frontend User interface
            const apiUser = response.user
            const user: User = {
                id: apiUser.id,
                username: apiUser.username,
                email: apiUser.email,
                firstName: apiUser.firstName,
                lastName: apiUser.lastName,
                roles: apiUser.roles as UserRole[],
                status: apiUser.status as any,
                avatar: apiUser.avatar,
                avatarAlt: apiUser.avatarAlt,
                proImage: apiUser.proImage,
                proImageAlt: apiUser.proImageAlt,
                banner: apiUser.banner,
                bannerAlt: apiUser.bannerAlt,
                modules: apiUser.modules || []
            }

            currentUser.value = user
            return user
        } catch (e: any) {
            console.error('Login error:', e)
            throw e
        }
    }

    const initAuth = async () => {
        const token = useCookie('auth_token', { path: '/' })
        const refresh = useCookie('refresh_token', { path: '/', maxAge: 60 * 60 * 24 * 7 })

        if (!token.value && !refresh.value) return

        // If we have no access token but have a refresh token, try silent refresh
        if (!token.value && refresh.value) {
            try {
                await refreshSession()
                return
            } catch (e) {
                console.error('Initial silent refresh failed:', e)
                refresh.value = null
                return
            }
        }

        try {

            const response = await $api<{ user: any }>('/auth/me')

            if (response && response.user) {
                const apiUser = response.user
                const user: User = {
                    id: apiUser.id,
                    username: apiUser.username,
                    email: apiUser.email,
                    firstName: apiUser.firstName,
                    lastName: apiUser.lastName,
                    roles: apiUser.roles as UserRole[],
                    status: apiUser.status as any,
                    avatar: apiUser.avatar,
                    proImage: apiUser.proImage,
                    banner: apiUser.banner,
                    birthDate: apiUser.birthDate,
                    workDate: apiUser.workDate,
                    isActive: apiUser.isActive,
                    bio: apiUser.bio,
                    jobTitle: apiUser.jobTitle,
                    workplaces: apiUser.workplaces,
                    linkedin: apiUser.linkedin,
                    avatarAlt: apiUser.avatarAlt,
                    proImageAlt: apiUser.proImageAlt,
                    bannerAlt: apiUser.bannerAlt,
                    modules: apiUser.modules || [],
                    createdAt: apiUser.createdAt
                }
                currentUser.value = user
            }
        } catch (e) {
            console.error('Failed to restore session:', e)

            // If main token expired, try to refresh
            const refreshCookie = useCookie('refresh_token')
            if (refreshCookie.value) {
                try {
                    await refreshSession()
                } catch (refreshErr) {
                    console.error('Silent refresh failed:', refreshErr)
                    refreshCookie.value = null
                }
            }
        }
    }

    const refreshSession = async () => {
        const refreshCookie = useCookie('refresh_token', { path: '/', maxAge: 60 * 60 * 24 * 7 })
        if (!refreshCookie.value) return

        try {
            const response = await $api<{ token: string, refreshToken: string, user: any }>('/auth/refresh-token', {
                method: 'POST',
                body: { refreshToken: refreshCookie.value }
            })

            // Update tokens
            useCookie('auth_token', { path: '/' }).value = response.token
            refreshCookie.value = response.refreshToken

            // Update user
            const apiUser = response.user
            currentUser.value = {
                id: apiUser.id,
                username: apiUser.username,
                email: apiUser.email,
                firstName: apiUser.firstName,
                lastName: apiUser.lastName,
                roles: apiUser.roles as UserRole[],
                status: apiUser.status as any,
                avatar: apiUser.avatar,
                proImage: apiUser.proImage,
                banner: apiUser.banner,
                modules: apiUser.modules || []
            }

            return currentUser.value
        } catch (e) {
            refreshCookie.value = null
            throw e
        }
    }

    const logout = async () => {
        const tokenCookie = useCookie('auth_token', { path: '/' })
        const refreshCookie = useCookie('refresh_token', { path: '/', maxAge: 60 * 60 * 24 * 7 })

        if (refreshCookie.value) {
            try {
                // Try to revoke on server
                await $api('/auth/revoke-token', {
                    method: 'POST',
                    body: { refreshToken: refreshCookie.value }
                })
            } catch (e) {
                console.warn('Failed to revoke token on server', e)
            }
        }

        tokenCookie.value = null
        refreshCookie.value = null
        currentUser.value = null
        const router = useRouter()
        router.push('/login')
    }

    const updateProfile = async (updates: Partial<User> & { avatarFile?: File | null, bannerFile?: File | null }) => {
        if (!currentUser.value) return

        try {
            // Use the real API update function
            const updated = await updateUser(currentUser.value.id, updates)

            // Update current user state
            currentUser.value = updated
            return updated
        } catch (e) {
            console.error('Failed to update profile:', e)
            throw e
        }
    }

    const removeUser = async (id: string) => {
        const index = users.value.findIndex(u => u.id === id)
        const user = index !== -1 ? users.value[index]! : null

        // If user is pending with token, delete from CSV
        if (user && user.status === 'Pending' && user.token) {
            await $fetch('/api/tokens', {
                method: 'DELETE',
                body: { token: user.token }
            })
        } else {
            // Delete ACTIVE user via API - always call even if not in local cache
            await $api(`/users/${id}`, { method: 'DELETE' })
        }

        // Remove from local cache if exists
        if (index !== -1) {
            users.value.splice(index, 1)
        }

        logAction({
            action: 'DELETE',
            entityType: 'Utilisateur',
            entityId: id,
            details: `Suppression de l'utilisateur "${user?.firstName || 'Unknown'} ${user?.lastName || ''}"`,
            snapshot: user
        })
    }

    const updateUser = async (id: string, updates: Partial<User> & { avatarFile?: File | null, bannerFile?: File | null, proImageFile?: File | null }) => {
        const formData = new FormData()
        if (updates.username) formData.append('username', updates.username)
        if (updates.firstName) formData.append('firstName', updates.firstName)
        if (updates.lastName) formData.append('lastName', updates.lastName)
        if (updates.email) formData.append('email', updates.email)
        if (updates.jobTitle) formData.append('jobTitle', updates.jobTitle)
        if (updates.workplaces) formData.append('workplaces', updates.workplaces)
        if (updates.bio) formData.append('bio', updates.bio)
        if (updates.linkedin) formData.append('linkedin', updates.linkedin)
        if (updates.birthDate) formData.append('birthDate', updates.birthDate)
        if (updates.workDate) formData.append('workDate', updates.workDate)
        if (updates.isActive !== undefined) formData.append('isActive', String(updates.isActive))

        // Check permissions before sending restricted fields
        const isAdmin = currentUser.value?.roles.includes('SuperAdmin') || currentUser.value?.roles.includes('Admin')

        if (updates.roles && isAdmin) {
            updates.roles.forEach(r => formData.append('roles', r))
        }

        if (updates.modules !== undefined && isAdmin) {
            if (updates.modules.length === 0) {
                // Send explicit clear flag when modules array is empty
                formData.append('clearModules', 'true')
            } else {
                updates.modules.forEach(m => formData.append('modules', m))
            }
        }

        if (updates.avatarFile) {
            formData.append('avatar', updates.avatarFile)
        }

        if (updates.proImageFile) {
            formData.append('proImage', updates.proImageFile)
        }

        if (updates.bannerFile) {
            formData.append('banner', updates.bannerFile)
        }

        if (updates.createdAt) {
            formData.append('createdAt', updates.createdAt)
        }

        // Alt Texts
        if (updates.avatarAlt !== undefined) formData.append('avatarAlt', updates.avatarAlt)
        if (updates.proImageAlt !== undefined) formData.append('proImageAlt', updates.proImageAlt)
        if (updates.bannerAlt !== undefined) formData.append('bannerAlt', updates.bannerAlt)

        const updatedUser = await $api<User>(`/users/${id}`, {
            method: 'PUT',
            body: formData
        })

        // Update local state if exists
        const index = users.value.findIndex(u => u.id === id)
        if (index !== -1) {
            // Merge response
            users.value[index] = { ...users.value[index], ...updatedUser }
        }

        logAction({
            action: 'UPDATE',
            entityType: 'Utilisateur',
            entityId: id,
            details: `Mise à jour de l'utilisateur "${updatedUser.firstName} ${updatedUser.lastName}"`,
        })

        return updatedUser
    }

    const restoreUser = async (user: User) => {
        await new Promise(resolve => setTimeout(resolve, 300))
        // Check if ID already exists
        if (users.value.find(u => u.id === user.id)) {
            throw new Error('User ID conflict or already restored')
        }
        users.value.push(user)
    }

    const fetchUserProjects = async (userId: string) => {
        return await $api<any[]>(`/users/${userId}/projects`)
    }

    const fetchUserFormations = async (userId: string) => {
        return await $api<any[]>(`/users/${userId}/formations`)
    }

    const updateUserFormations = async (userId: string, slugs: string[]) => {
        return await $api(`/users/${userId}/formations`, {
            method: 'POST',
            body: slugs
        })
    }

    const changePassword = async (oldPassword: string, newPassword: string) => {
        return await $api('/auth/change-password', {
            method: 'POST',
            body: { oldPassword, newPassword }
        })
    }

    const requestPasswordReset = async (email: string) => {
        return await $api<{ message: string }>('/auth/forgot-password', {
            method: 'POST',
            body: { email }
        })
    }

    return {
        currentUser,
        getUsers,
        refreshPending,
        inviteUser,
        completeSetup,
        login,
        logout,
        initAuth,
        updateProfile,
        removeUser,
        updateUser,
        restoreUser,
        fetchUserProjects,
        fetchUserFormations,
        updateUserFormations,
        getInvitationInfo,
        changePassword,
        requestPasswordReset,
        refreshSession
    }
}
